import { makeRequest } from './widget-facade';
import athenaConfig from '../config';
import { ERROR_KEYS, ERROR_MESSAGES } from '../constants';
import AmplitudeUtils from './amplitude-utils';
import asyncRetry from 'async-retry';
import { PasswordResetError } from './errors';

const { athenaOneResetPasswordLambdaBaseUrl } = athenaConfig;

/**
 * Check if error is retryable (5xx server errors or no response)
 * Okta's invokeApiMethod returns errors with status in different places:
 * - error.xhr.status (wrapped errors)
 * - error.status (direct errors)
 * - error.responseJSON.status (some error formats)
 */
function isRetryableError(error: any): boolean {
  if (!error) {
    return true; // No response - retry
  }

  const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;
  return status >= 500 && status <= 599;
}

/**
 * Generic retry wrapper for password reset API calls
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
async function makeRequestWithRetry({
  url,
  args,
  retryEventName,
  username,
}: {
  url: string;
  args: any;
  retryEventName: string;
  username: string;
}): Promise<any> {
  return asyncRetry(
    async (bail, attemptNumber) => {
      try {
        const result = await makeRequest({
          url,
          method: 'POST',
          args,
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return result;
      }
      catch (error: any) {
        const status = error?.xhr?.status || error?.status || error?.responseJSON?.status;

        // Log retry attempt
        if (attemptNumber > 1) {
          AmplitudeUtils.logCustomEvent(retryEventName, {
            username,
            attemptNumber,
            status,
            isRetryable: isRetryableError(error),
          });
        }

        // If it's a retryable error (5xx), throw to trigger retry
        if (isRetryableError(error)) {
          throw error;
        }
        // Otherwise, bail out (don't retry)
        bail(error);
        return; // TypeScript needs this
      }
    },
    { retries: 2, factor: 1, minTimeout: 2000 }
  );
}

export interface EligibilityCheckResponse {
  eligible: boolean;
  useLegacyWorkflow?: boolean;
  message?: string;
}

/**
 * Initialize password reset flow - check user eligibility
 * Lambda will also assign user to appropriate Okta group
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordResetInit({
  username,
  clientId,
}: {
  username: string;
  clientId: string;
}): Promise<EligibilityCheckResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset-init`,
      args: { username, clientid: clientId },
      retryEventName: 'passwordResetInitRetry',
      username,
    });

    return {
      eligible: response.eligible,
      useLegacyWorkflow: response.useLegacyWorkflow,
      message: response.message,
    };
  }
  catch (error) {
    // Throw error in the format the widget expects
    throw new PasswordResetError(ERROR_MESSAGES[ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR]);
  }
}

/**
 * Call post-forgot-password Lambda endpoint
 * This is called after the user submits their email/username on the forgot password screen
 * Note: This is called AFTER password reset succeeds, so we don't want to break the workflow if it fails
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordUpdated({
  emailOrLogin,
  stateHandle,
}: {
  emailOrLogin: string;
  stateHandle: string;
}): Promise<any> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaOneResetPasswordLambdaBaseUrl}/user-password-updated`,
      args: { emailOrLogin, stateHandle },
      retryEventName: 'passwordResetCompletedRetry',
      username: emailOrLogin,
    });

    return response;
  }
  catch (error) {
    // Log the error but don't throw - this is called after password reset succeeds
    // so we don't want to break the workflow
    AmplitudeUtils.logCustomEvent('passwordResetUserPasswordUpdatedFailed', {
      username: emailOrLogin,
      stateHandle,
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    return undefined;
  }
}

export interface PasswordResetResponse {
  success?: {
    name: string;
    href: string;
  };
  sessionToken?: string;
}

/**
 * Complete password reset by calling Lambda endpoint
 * This endpoint handles both password reset validation and cleanup
 * For forgot password flow, pass empty string for oldPassword
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordReset({
  username,
  oldPassword,
  newPassword,
  stateHandle,
  symantecSecurityCode,
  clientId,
}: {
  username: string;
  oldPassword: string;
  newPassword: string;
  stateHandle: string;
  symantecSecurityCode?: string;
  clientId?: string;
}): Promise<PasswordResetResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset`,
      args: {
        username,
        oldPassword,
        newPassword,
        stateHandle,
        symantecSecurityCode,
        clientId,
      },
      retryEventName: 'passwordResetCompletionRetry',
      username,
    });

    // Return the full response from the lambda (which contains the full IDX response from Okta)
    return response;
  }
  catch (error: any) {
    // Extract error message from response
    const errorMessage =
      error?.xhr?.responseJSON?.message ||
      error?.responseJSON?.message ||
      error?.message ||
      ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

    throw new PasswordResetError(errorMessage);
  }
}

export interface SymantecVIPCheckResponse {
  shouldPrompt: boolean;
  username?: string;
}

/**
 * Check if user should be prompted for Symantec VIP code
 * Calls Lambda endpoint which validates username against stateHandle
 * and then determines if user is EPCS user
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function shouldPromptForSymantecVIP({
  username,
  stateHandle,
}: {
  username: string;
  stateHandle: string;
}): Promise<SymantecVIPCheckResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/should-prompt-for-symantec-vip`,
      args: {
        username,
        stateHandle,
      },
      retryEventName: 'passwordResetSymantecVIPCheckRetry',
      username,
    });

    return {
      shouldPrompt: response.shouldPrompt,
      username: response.username,
    };
  }
  catch (error: any) {
    // Log the error but don't throw - default to not prompting
    AmplitudeUtils.logCustomEvent('passwordResetSymantecVIPCheckFailed', {
      stateHandle: stateHandle.substring(0, 10) + '...', // Log partial for security
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    // Default to not prompting for Symantec VIP if check fails
    return {
      shouldPrompt: false,
    };
  }
}

export interface ForgotUsernameResponse {
  success: boolean;
}

/**
 * Send forgot username email
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function forgotUsername({
  email,
  clientId,
}: {
  email: string;
  clientId: string;
}): Promise<ForgotUsernameResponse> {
  try {
    await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/forgot-username`,
      args: { email, clientId },
      retryEventName: 'forgotUsernameRetry',
      username: email,
    });

    // If request succeeds (200 OK), return success
    return { success: true };
  }
  catch (error: any) {
    // Extract error message from response
    const errorMessage =
      error?.xhr?.responseJSON?.message ||
      error?.responseJSON?.message ||
      error?.message ||
      ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];

    throw new PasswordResetError(errorMessage);
  }
}
